import * as Sentry from '@sentry/react';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { AlexIdUser } from 'Containers/App/types';
import { makeNewCommExternalId, normalizeLanguages, resolveElections } from 'Containers/App/utils';
import {
  claimsSummaryPollingStopped,
  getClientSurveyFailure,
  getClientSurveySuccess,
  getJvpClientSurveyFailure,
  getJvpClientSurveySuccess,
  getJvpEligibilityQuestionsFailure,
  getJvpEligibilityQuestionsSuccess,
  getLatestClaimsSummaryFailure,
  getLatestClaimsSummarySuccess,
} from 'Containers/ProfilePage/actions';
import { GET_LATEST_CLAIMS_SUMMARY_REQUEST, SANITIZE_PROFILE_INPUTS } from 'Containers/ProfilePage/constants';
import { makeGetSelectedEnrollmentEventId } from 'Containers/ProfilePage/selectors';
import {
  apiAAPIGetUserBySessionId,
  apiJVPCreateOrUpdateEnrollmentEvent,
  apiJVPCreateUser,
  apiJVPGetBootstrap,
  apiJVPGetUser,
  apiJVPPulseSession,
  getClientSurvey,
  getClientSurveyMetadata,
  getConfig,
  getDataRevisionConfig,
  getElections,
  getEligibilityQuestions,
  getLatestClaimsSummary,
  updateJVPUserAttributes,
} from 'Utils/api';
import * as API_TYPES from 'Utils/apiTypes';
import {
  Election,
  GetJVPEligibilityQuestionsResponse,
  JVPBootstrapPublicationInfo,
  JVPEligibilityQuestion,
  JVPUser,
} from 'Utils/apiTypes';
import Logger from 'Utils/logger';
import { medicareDestinationFromConfig } from 'Utils/medicare';
import { pollUntil } from 'Utils/poller';
import { validateClaimsSummaryResponse } from 'Utils/validators';

import {
  getAAPIUserFailure,
  getAAPIUserSuccess,
  getJVPBootstrapFailure,
  getJVPBootstrapSuccess,
  getJVPUserFailure,
  getJVPUserSuccess,
  handleProfileComplete,
  jvpSessionExpired,
  loadConfigFailure,
  setMedicareDestination,
  loadConfigSuccess,
  setEnrollmentEventProfileCompleteFailure,
  setEnrollmentEventProfileCompleteSuccess,
  setEnrollmentEventTypeFailure,
  setEnrollmentEventTypeSuccess,
  setValidatedSegmentSlug,
} from './actions';
import {
  GET_AAPI_USER_REQUEST,
  GET_JVP_BOOTSTRAP,
  GET_JVP_BOOTSTRAP_SUCCESS,
  LOAD_CONFIG,
  LOAD_CONFIG_SUCCESS,
  PULSE_JVP_SESSION,
  SET_ALEX_ID_UUID,
  SET_ENROLLMENT_EVENT_PROFILE_COMPLETE,
  SET_ENROLLMENT_EVENT_TYPE,
  UPDATE_USER_UI_SETTINGS,
} from './constants';
import { makeSelectCommercialField, makeSelectGlobalField, makeSelectJvpField } from './selectors';
import { DEFAULT_MEDICARE_DESTINATION, MedicareDestination } from '../../constants';

export function* getXhrConfig() {
  try {
    const resp: API_TYPES.GetConfigResponse | null = yield call(getConfig);
    if (resp && Object.keys(resp.config).length > 0) {
      yield put(loadConfigSuccess(resp));
    } else {
      yield put(loadConfigFailure(new Error('client not found')));
    }
  } catch (err) {
    yield put(loadConfigFailure(err as Error));
  }
}

const getTarget = () => {
  const { DEPLOY_ENV } = window._env_;
  if (DEPLOY_ENV === 'uat') {
    return 'preview';
  }
  if (DEPLOY_ENV === 'prod') {
    return 'live';
  }
  return 'internal_preview';
};

export function* getXhrJVPBootstrap(action) {
  let { userId } = action;
  const { employerSlug } = action;

  try {
    if (!userId) {
      // Should we just use the policyholder's external_id here?
      // Otherwise, the jvpUser.external_id and policyholder.external_id will not match
      userId = makeNewCommExternalId();
    }

    yield call(() => apiJVPCreateUser(userId));

    // Fetch (full) JVP User (with dependents) while we're here
    try {
      const jvpUser = yield call(() => apiJVPGetUser(userId));
      yield put(getJVPUserSuccess(jvpUser));
    } catch (userError) {
      yield put(getJVPUserFailure(new Error('Failed to fetch JVP User With Dependents')));
      Logger.log(`JVP user request failed: ${userError}`);
    }

    const unvalidatedSegmentSlug: string = yield select(makeSelectGlobalField('unvalidatedSegmentSlug'));

    let bootstrap: API_TYPES.JVPBootstrap | undefined;
    try {
      bootstrap = yield call(() =>
        apiJVPGetBootstrap(
          userId,
          employerSlug,
          getTarget(),
          new Date().toISOString().slice(0, 10), // YYYY-MM-DD
          unvalidatedSegmentSlug,
        ),
      );
    } catch (err) {
      Logger.log(`Bootstrap request failed: ${err}`);
    }

    // Filter publications that aren't set to use comm-api
    const active = bootstrap?.active?.publication?.use_comm_api ? bootstrap?.active : null;
    const upcoming = bootstrap?.upcoming?.publication?.use_comm_api ? bootstrap?.upcoming : null;

    // `conversation.languages` is the enabled set: platform-api's union of the active and
    // upcoming publications' languages, in publication order. platform-api applies the
    // legacy `alex_go_language` fallback for publications that predate
    // `alex_go_languages` (GoSettings#languages), so it's used as-is here.
    const enabledLanguages = normalizeLanguages(bootstrap?.conversation?.languages);

    let validatedSegmentSlug: string | null = null;
    const normalizedSegmentSlug = unvalidatedSegmentSlug?.toLowerCase();

    if (active && active.segment) {
      if (active.segment.slug === normalizedSegmentSlug) {
        validatedSegmentSlug = normalizedSegmentSlug;
      }
    }

    if (!validatedSegmentSlug && upcoming && upcoming.segment) {
      if (upcoming.segment.slug === normalizedSegmentSlug) {
        validatedSegmentSlug = normalizedSegmentSlug;
      }
    }

    if (!active && !upcoming) {
      yield put(getJVPBootstrapFailure(new Error('No active configurations found')));
    } else {
      yield put(setValidatedSegmentSlug(validatedSegmentSlug));
      yield put(
        getJVPBootstrapSuccess({
          user: bootstrap?.user as JVPUser,
          employerId: bootstrap?.employer?.jv_employer_uuid || '',
          employerName: bootstrap?.employer?.employer_name || '',
          employeeId: bootstrap?.employee?.id || '',
          active,
          upcoming,
          enabledLanguages,
          validatedSegmentSlug,
          employee: bootstrap?.employee || null,
        }),
      );
    }
  } catch (err) {
    Logger.log(`Unexpected error: ${err}`);
    yield put(getJVPBootstrapFailure(err as unknown as Error));
  }
}

export function* getXhrJVPUser(action) {
  const { alex_id_uuid } = action;
  try {
    const jvpUser = yield call(() => apiJVPGetUser(alex_id_uuid));
    yield put(getJVPUserSuccess(jvpUser));
  } catch (userError) {
    yield put(getJVPUserFailure(new Error('Failed to fetch JVP User With Dependents')));
    Logger.log(`JVP user request failed: ${userError}`);
  }
}

export function* postXhrSetEnrollmentEventProfileComplete(action) {
  try {
    const validatedSegmentSlug: string = yield select(makeSelectGlobalField('validatedSegmentSlug'));
    let elections: Election[] = [];

    const response: API_TYPES.JVPEnrollmentEvent = yield call(() =>
      apiJVPCreateOrUpdateEnrollmentEvent(
        action.employeeId,
        action.publicationId,
        action.eventType,
        action.profileComplete,
        validatedSegmentSlug,
      ),
    );
    if (response.id) {
      // Fetch previous year selected plans
      const electionsResp = yield call(() => getElections(response.id));
      const fetchedElections: Election[] = electionsResp?.elections;
      const currentElections: Election[] = yield select(makeSelectGlobalField('elections'));
      const isCoverageWaived: boolean = yield select(makeSelectCommercialField('isCoverageWaived'));

      // preserve Redux for pre-selected plan when JVP returns waived-only due to custom plan selection.
      elections = resolveElections(fetchedElections, currentElections, isCoverageWaived);
    }
    yield put(setEnrollmentEventProfileCompleteSuccess(response, elections));
  } catch (err) {
    yield put(setEnrollmentEventProfileCompleteFailure(err as unknown as Error));
  }
}

export function* postXhrSetEnrollmentEventType(action) {
  try {
    const validatedSegmentSlug: string = yield select(makeSelectGlobalField('validatedSegmentSlug'));

    const response: API_TYPES.JVPEnrollmentEvent = yield call(() =>
      apiJVPCreateOrUpdateEnrollmentEvent(
        action.employeeId,
        action.publicationId,
        action.eventType,
        false,
        validatedSegmentSlug,
      ),
    );
    yield put(setEnrollmentEventTypeSuccess(response));
  } catch (err) {
    yield put(setEnrollmentEventTypeFailure(err as unknown as Error));
  }
}

export function* postXhrJVPPulseSession(action) {
  try {
    yield call(() => apiJVPPulseSession(action.sessionId));
  } catch (err) {
    // A 401 means the AAPI session has expired: mark it expired, which stops the pulse loop and
    // surfaces the "session has expired" modal.
    if ((err as { status?: number })?.status === 401) {
      yield put(jvpSessionExpired());
    }

    // Any other non-200 (403, transient 5xx, deploys, network blips) is swallowed. We must NOT
    // rethrow — an uncaught error in a takeLatest worker propagates up and terminates the whole
    // getAppSaga watcher group (there's no onError on the saga middleware), which would silently
    // kill config/profile/claims/etc. for the rest of the session. checkStatus already reported
    // the error to Sentry, and the 1-minute interval simply retries on the next tick.
  }
}

function* pollLatestClaimsSummary() {
  const enrollmentEventId = yield select(makeGetSelectedEnrollmentEventId());
  try {
    const response = yield pollUntil(
      () => getLatestClaimsSummary(enrollmentEventId),
      validateClaimsSummaryResponse,
      1000,
      120000,
    );
    // TODO: What happens if status === 'error'?
    yield put(getLatestClaimsSummarySuccess(response));
  } catch (error) {
    yield put(getLatestClaimsSummaryFailure(error));
  } finally {
    yield put(claimsSummaryPollingStopped());
  }
}

/**
 * Given a session-id AAPI (should) know about, try-get an auth0_uuid in return.
 */
export function* postXhrAAPIGetUserBySessionId(action) {
  try {
    const response: AlexIdUser = yield call(() => apiAAPIGetUserBySessionId(action.sessionId));
    yield put(getAAPIUserSuccess(response));
  } catch (err) {
    yield put(getAAPIUserFailure(err as unknown as Error));
  }
}

export function* postJVPUserAttributeUpdate(action) {
  const user_id = yield select(makeSelectJvpField('userId'));
  try {
    yield call(() =>
      updateJVPUserAttributes({
        user_id,
        attributes: {
          ...action.uiSettings,
        },
      }),
    );
  } catch (err) {
    Logger.log(`Failed to update JVP user ${user_id}`);
  }
}

export function* getXhrClientSurvey() {
  try {
    const resp: API_TYPES.ClientSurveySchema = yield call(getClientSurvey);

    yield put(getClientSurveySuccess({ upcoming: resp }));
  } catch (err) {
    yield put(getClientSurveyFailure(err));
  }
}

function* retrieveClientSurveyWithMetadata(enrollmentEventId: string) {
  const survey: API_TYPES.ClientSurveySchema = yield call(() => getClientSurvey(enrollmentEventId));
  const metadata: API_TYPES.ClientSurveyMetadataResponse = yield call(() =>
    getClientSurveyMetadata(enrollmentEventId),
  );

  if (!survey) {
    return null;
  }

  survey.properties = Object.entries(survey.properties).reduce(
    (acc, [key, val]) => ({
      ...acc,
      [key]: {
        ...val,
        hideChoices: metadata?.data?.find((data) => key === data.client_survey_id)?.hide_choices || false,
      },
    }),
    {},
  );
  return survey;
}

export function* getXhrJvpClientSurvey() {
  const active: API_TYPES.JVPBootstrapPublicationInfo | null = yield select(makeSelectJvpField('active'));
  const upcoming: API_TYPES.JVPBootstrapPublicationInfo | null = yield select(makeSelectJvpField('upcoming'));

  const result = {
    active: null,
    upcoming: null,
  };

  try {
    if (active?.enrollment_event.id) {
      result.active = yield call(retrieveClientSurveyWithMetadata, active?.enrollment_event.id);
    }

    if (upcoming?.enrollment_event.id) {
      result.upcoming = yield call(retrieveClientSurveyWithMetadata, upcoming?.enrollment_event.id);
    }

    yield put(getJvpClientSurveySuccess(result));
  } catch (err) {
    yield put(getJvpClientSurveyFailure(err));
  }
}

export function* getJvpEligibilityQuestions() {
  const activePublicationInfo: JVPBootstrapPublicationInfo | null = yield select(
    makeSelectJvpField('active'),
  );

  const upcomingPublicationInfo: JVPBootstrapPublicationInfo | null = yield select(
    makeSelectJvpField('upcoming'),
  );

  const eligibilityQuestions: Record<'active' | 'upcoming', JVPEligibilityQuestion[] | null> = {
    active: null,
    upcoming: null,
  };
  if (activePublicationInfo) {
    try {
      const publicationId = activePublicationInfo.publication.jv_publication_uuid;
      const result: GetJVPEligibilityQuestionsResponse = yield call(() =>
        getEligibilityQuestions(publicationId),
      );
      eligibilityQuestions.active = result.eligibility_questions;
    } catch (err) {
      yield put(getJvpEligibilityQuestionsFailure(err));
    }
  }

  if (upcomingPublicationInfo) {
    try {
      const publicationId = upcomingPublicationInfo.publication.jv_publication_uuid;
      const result: GetJVPEligibilityQuestionsResponse = yield call(() =>
        getEligibilityQuestions(publicationId),
      );
      eligibilityQuestions.upcoming = result.eligibility_questions;
    } catch (err) {
      yield put(getJvpEligibilityQuestionsFailure(err));
    }
  }

  yield put(getJvpEligibilityQuestionsSuccess(eligibilityQuestions));
}

export function* getMedicareDestination(publicationInfo: JVPBootstrapPublicationInfo | null) {
  const dataRevisionLocation = publicationInfo?.publication.data_revision_location;
  if (!dataRevisionLocation) {
    return DEFAULT_MEDICARE_DESTINATION;
  }

  try {
    const config: API_TYPES.DataRevisionConfig = yield call(getDataRevisionConfig, dataRevisionLocation);
    return medicareDestinationFromConfig(config);
  } catch (err) {
    // A failed load must not break the page. Use the default destination.
    // checkStatus has already reported HTTP errors; report network failures and timeouts here.
    if (!(err as { status?: number })?.status) {
      Sentry.captureException(err);
    }
    return DEFAULT_MEDICARE_DESTINATION;
  }
}

// Stores each destination as soon as it arrives, so a slow publication never delays the other.
export function* loadMedicareDestination(
  publicationKey: 'active' | 'upcoming',
  publicationInfo: JVPBootstrapPublicationInfo | null,
) {
  const destination: MedicareDestination = yield call(getMedicareDestination, publicationInfo);
  yield put(setMedicareDestination(publicationKey, destination));
}

// The destination is read from each publication's data revision (configuration.json).
export function* getMedicareDestinations() {
  const active: JVPBootstrapPublicationInfo | null = yield select(makeSelectJvpField('active'));
  const upcoming: JVPBootstrapPublicationInfo | null = yield select(makeSelectJvpField('upcoming'));

  yield all([
    call(loadMedicareDestination, 'active', active),
    call(loadMedicareDestination, 'upcoming', upcoming),
  ]);
}

export function* getLegacyClientSurvey() {
  const config: API_TYPES.ClientConfig = yield select(makeSelectGlobalField('config'));

  // if builder customer, perform no-op.
  if (config?.builder_customer_key) {
    return;
  }

  // legacy picwell client
  if (config && !config.builder_customer_key) {
    yield call(getXhrClientSurvey);
  }
  // user entered the wrong client
  else {
    Logger.log('Invalid client id');
  }
}

function* handleCompleteProfile() {
  yield put(handleProfileComplete());
}

// Individual exports for testing
export default function* getAppSaga() {
  yield all([
    takeLatest(LOAD_CONFIG, getXhrConfig),
    takeLatest(LOAD_CONFIG_SUCCESS, getLegacyClientSurvey),
    takeLatest(GET_JVP_BOOTSTRAP_SUCCESS, getXhrJvpClientSurvey),
    // JVP ACTIONS
    takeLatest(GET_JVP_BOOTSTRAP, getXhrJVPBootstrap),
    takeLatest(SET_ENROLLMENT_EVENT_TYPE, postXhrSetEnrollmentEventType),
    takeLatest(GET_JVP_BOOTSTRAP_SUCCESS, getJvpEligibilityQuestions),
    takeLatest(GET_JVP_BOOTSTRAP_SUCCESS, getMedicareDestinations),
    takeLatest(SET_ENROLLMENT_EVENT_PROFILE_COMPLETE, postXhrSetEnrollmentEventProfileComplete),
    takeLatest(PULSE_JVP_SESSION, postXhrJVPPulseSession),
    takeLatest(GET_AAPI_USER_REQUEST, postXhrAAPIGetUserBySessionId),
    takeLatest(SANITIZE_PROFILE_INPUTS, handleCompleteProfile),
    takeLatest(UPDATE_USER_UI_SETTINGS, postJVPUserAttributeUpdate),
    takeLatest(SET_ALEX_ID_UUID, getXhrJVPUser),
    takeLatest(GET_LATEST_CLAIMS_SUMMARY_REQUEST, pollLatestClaimsSummary),
  ]);
}
